import React, {
  useEffect,
  useMemo, useRef, useState,
} from 'react';

import {
  CopyAllRounded,
  EmailRounded, PhoneRounded,
} from '@mui/icons-material';
import DescriptionIcon from '@mui/icons-material/Description';
import GroupIcon from '@mui/icons-material/Group';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import {
  Box,
  CircularProgress,
  Grid,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Switch,
  Typography,
  useTheme,
} from '@mui/material';
import {
  gridFilteredSortedRowIdsSelector, useGridApiRef,
} from '@mui/x-data-grid';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import _ from 'lodash';

import Button from '../../components/Button';
import DataGrid from '../../components/DataGrid';
import DateNavigator from '../../components/DateNavigator';
import DetailsModal, { Fields, Values } from '../../components/DetailsModal';
import LessonScheduleView from '../../components/LessonScheduleView';
import PageContent from '../../components/PageContent';
import SearchField from '../../components/SearchField';
import SkeletonStack from '../../components/SkeletonStack';
import Tabs from '../../components/Tabs';
import {
  useGetClassesQuery,
  useGetLessonsQuery,
  useLazyGetStudentDetailsByClassesQuery,
  useNewClassMutation,
} from '../../services/classes';
import { Class } from '../../services/classes/types';
import { useGetCoursesQuery } from '../../services/courses';
import { useGetStreamsQuery } from '../../services/streams';
import { UserGroup } from '../../types/common';
import compareDayLabels from '../../utils/common/compareDayLabels';
import {
  GRADES, MAX_CLASS_SIZE,
  SUBJECTS,
} from '../../utils/common/constants';
import useSuccess from '../../utils/react/hooks/useSuccess';
import useUserGroups from '../../utils/react/hooks/useUserGroups';

import {
  FilterContainer, StyledBackdrop,
} from './index.styles';

dayjs.extend(customParseFormat);

const ALL = 'all';

const INITIAL_TAB_OPTIONS = [
  {
    key: ALL,
    label: 'All',
  },
];

const INITIAL_NEW_FORM = {
  subject: '',
  course: '',
  stream: '',
  day: -1,
  time: '',
  location: 'Chatswood',
  id: '',
};

const NEW_FORM_FIELDS: Fields = {
  subject: {
    editable: true,
    type: 'select',
    typeOptions: Object.entries(SUBJECTS).map(([key, value]) => ({
      label: value.label,
      value: key,
    })),
  },
  course: {
    editable: true,
    type: 'autocomplete',
    typeOptions: [],
  },
  stream: {
    editable: true,
    type: 'autocomplete',
    typeOptions: [],
  },
  day: {
    editable: true,
    type: 'autocomplete',
    typeOptions: [
      {
        label: 'Undetermined',
        value: -1,
      },
      ...Array.from({ length: 7 }, (x, i) => ({
        label: dayjs().day(i).format('dddd'),
        value: i,
      })),
    ],
  },
  time: {
    editable: true,
    type: 'time',
  },
  location: {
    editable: false,
    type: 'text',
  },
  id: {
    editable: true,
    type: 'text',
    label: 'Class name',
  },
};

interface ClassData extends Class {
  grade: string;
  searchStr: string;
  timeLabel: string;
  courseLabel: string;
  streamLabel: string;
  dayLabel: string;
}

const createOptions = (data: any[],
  keySelector: (item: any) => string,
  labelSelector: (item: any) => string,
  sortBy?: 'key' | 'label' | 'day') => [
  ...INITIAL_TAB_OPTIONS,
  ...data.map((item) => ({
    key: keySelector(item),
    label: labelSelector(item),
  })).sort((a, b) => {
    if (sortBy === 'label') return a.label.localeCompare(b.label);
    if (sortBy === 'day') {
      // Change key = '0' to key = '7' so that Sunday is last
      const aKey = a.key === '0' ? '7' : a.key;
      const bKey = b.key === '0' ? '7' : b.key;
      return aKey.localeCompare(bKey);
    }
    if (sortBy === 'key') return a.key.localeCompare(b.key);
    return 0;
  }),
];

const filterClasses = (
  classes: ClassData[], filters: Record<string, string>, searchStr: string,
  hideFullClasses: boolean): ClassData[] => {
  let result = [...classes];
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== ALL) {
      result = result.filter((classItem) =>
        classItem[key as keyof ClassData]?.toString() === value,
      );
    }
  });
  // Remove spaces from search strings
  result = result.filter((classItem) => classItem.searchStr.replace(/\s/g, '').toLowerCase()
    .includes(searchStr.replace(/\s/g, '').toLowerCase()));
  // Remove archived classes
  result = result.filter((classItem) => classItem.active);
  // Remove full classes if hideFullClasses is true
  if (hideFullClasses) {
    result = result.filter((classItem) => classItem.studentCount < MAX_CLASS_SIZE);
  }
  return result;
};

const getClassCountColor = (count: number) => {
  if (count >= MAX_CLASS_SIZE) return 'red';
  if (MAX_CLASS_SIZE - count === 1) return 'yellow';
  return 'gray';
};

const ClassScheduleView = ({ date, classObj }: {date: dayjs.Dayjs, classObj: Class}) => {
  const { data: lessons } = useGetLessonsQuery({
    subject: classObj.subject, classId: classObj.classId,
  });

  return (
    <Box padding={0.5} minHeight={48}>
      <LessonScheduleView
        lessons={lessons?.data}
        startDatetime={date.valueOf()}
        minWeeks={6}
        maxWeeks={6}
        regularDay={classObj.day}
      />
    </Box>
  );
};

const StaffClassesPage = () => {
  const {
    data: allClasses,
    isLoading,
  } = useGetClassesQuery({});

  const theme = useTheme();
  const userGroups = useUserGroups();
  const isAdmin = userGroups.includes(UserGroup.Admins);

  const gridApiRef = useGridApiRef();

  const { data: streams, isLoading: isStreamsLoading } = useGetStreamsQuery({});

  const { data: courses, isLoading: isCoursesLoading } = useGetCoursesQuery({});

  const [gradeTab, setGradeTab] = useState(ALL);
  const [courseTab, setCourseTab] = useState(ALL);
  const [dayTab, setDayTab] = useState(ALL);
  const [timeTab, setTimeTab] = useState(ALL);

  const [classes, setClasses] = useState<ClassData[]>();

  const [searchStr, setSearchStr] = useState<string>('');

  const [newClassFormOpen, setNewClassFormOpen] = useState<boolean>(false);
  const [newClassForm, setNewClassForm] = useState(INITIAL_NEW_FORM);
  const [newClassFormFields, setNewClassFormFields] = useState<Fields>(NEW_FORM_FIELDS);

  const [date, setDate] = useState(dayjs());
  const [showSchedule, setShowSchedule] = useState(false);

  const [hideFullClasses, setHideFullClasses] = useState(false);

  const menuRef = useRef<HTMLButtonElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const [getClassesStudentDetails,
    { data: studentDetails,
      isFetching: isStudentDetailsFetching,
      isSuccess: isStudentDetailsSuccess }] = useLazyGetStudentDetailsByClassesQuery();

  const [newClass, {
    isLoading: isNewClassLoading,
    error: newClassError,
    isSuccess: isNewClassSuccess,
  }] = useNewClassMutation();

  // copy list of classes to clipboard including their time, subject and tutor
  const copyClassListToClipboard = () => {
    const visibleRowIds = gridFilteredSortedRowIdsSelector(
      gridApiRef.current.state, gridApiRef.current.instanceId);
    const visibleClasses = visibleRowIds.map((id) =>
      classes?.find((classItem) => classItem.id === id))
      .filter((x) => x) as ClassData[]; // Exclude classes that are filtered out

    // copy classes groups by course
    const classGroups = _.groupBy(visibleClasses, (classItem) => classItem.courseLabel);

    const classList = Object.entries(classGroups).map(([course, c]) => {
      const classListStr = c.map((classItem) =>
        `- ${classItem.dayLabel} ${classItem.timeLabel}${classItem.teacherName ? ` (${classItem.teacherName})` : ''}${classItem.studentCount >= MAX_CLASS_SIZE ? ' [FULL]' : ''}`).join('\n');
      return `${course}:\n${classListStr}`;
    }).join('\n\n');

    navigator.clipboard.writeText(classList);
  };

  const copyStudentDetailsToClipboard = (attribute: string) => {
    const visibleRowIds = gridFilteredSortedRowIdsSelector(
      gridApiRef.current.state, gridApiRef.current.instanceId);
    const visibleClasses = visibleRowIds.map((id) =>
      classes?.find((classItem) => classItem.id === id))
      .filter((x) => x) as ClassData[]; // Exclude classes that are filtered out

    const classIds = visibleClasses.map((classItem) => `${classItem.subject}#${classItem.classId}`);
    getClassesStudentDetails({ classIds, attribute }, true);
  };

  useEffect(() => {
    if (studentDetails !== undefined) {
      // Comma separate array
      const detailsString: string = studentDetails.data.join(', ');
      navigator.clipboard.writeText(detailsString);
    }
  }, [studentDetails]);

  useEffect(() => {
    if (isNewClassSuccess) {
      setNewClassFormOpen(false);
      setNewClassForm(INITIAL_NEW_FORM);
    }
  }, [isNewClassSuccess]);

  useEffect(() => {
    // Map classes into classData
    if (allClasses && courses && streams) {
      setClasses(allClasses.data.map((classItem) => {
        const course = courses.data.find((c) => c.id === classItem.course);
        const streamLabel = streams?.data.find((s) =>
          s.streamId === classItem.stream)?.shortLabel ?? '';
        const dayLabel = classItem.day === -1 ? 'Undetermined' : dayjs().day(classItem.day).format('dddd');
        const timeLabel = dayjs(classItem.time, 'HHmm').format('h:mm a');
        return {
          ...classItem,
          grade: course?.grade ?? '',
          timeLabel,
          courseLabel: course?.label ?? '',
          streamLabel,
          dayLabel,
          searchStr: `${classItem.teacherName}${streamLabel}${classItem.classId}`
           + `${course?.label}${course?.shortLabel}${dayLabel}${timeLabel}${classItem.schoolTag}`,
        };
      })
      // Sort by day and time, if time is undefined, sort by day
        .sort((a, b) => {
          if (a.day === b.day) {
            if (a.time === b.time) return 0;
            if (a.time === undefined) return 1;
            if (b.time === undefined) return -1;
            return a.time.localeCompare(b.time);
          }
          return compareDayLabels(a.dayLabel, b.dayLabel);
        }),
      );
    }
  }, [allClasses, courses, streams]);

  useEffect(() => {
    if (courses && streams) {
      setNewClassFormFields({
        ...NEW_FORM_FIELDS,
        ...(newClassForm.subject && {
          course: {
            ...newClassFormFields.course,
            typeOptions: courses.data.filter((course) => course.subject === newClassForm.subject)
              .map((course) => ({
                label: course.shortLabel,
                value: course.id,
              })),
          },
        }),
        ...(newClassForm.course ? {
          stream: {
            ...newClassFormFields.stream,
            typeOptions: streams.data.filter((stream) => stream.course === newClassForm.course)
              .map((stream) => ({
                label: stream.shortLabel,
                value: stream.streamId,
              })),
          },
        } : {
          stream: {
            ...newClassFormFields.stream,
            typeOptions: [],
          },
        }),
      });
    }
  }, [courses, streams, newClassForm.subject, newClassForm.course]);

  const coursesOptions = useMemo(() => {
    if (!courses) return INITIAL_TAB_OPTIONS;
    const data = gradeTab === ALL ?
      courses.data : courses.data.filter((course) => course.grade === gradeTab);
    return createOptions(data, (course) => course.id, (course) => (gradeTab === ALL ?
      course.label : SUBJECTS[course.subject].label), 'label');
  }, [courses, gradeTab]);

  const timeOptions = useMemo(() => {
    if (!allClasses) return INITIAL_TAB_OPTIONS;
    const uniqueTimes = [..._.uniq(allClasses.data.map((classItem) => classItem.time))];
    return createOptions(uniqueTimes, (time) => time || 0, (time) => dayjs(time, 'HHmm').format('h:mm a'), 'key');
  }, [allClasses]);

  const dayOptions = useMemo(() => {
    if (!allClasses) return INITIAL_TAB_OPTIONS;
    const uniqueDays = [..._.uniq(allClasses.data.map((classItem) => classItem.day))];
    return createOptions(uniqueDays, (day) => day?.toString() || '-1', (day) => (day >= 0 ? dayjs().day(day).format('ddd') : 'Undetermined'), 'day');
  }, [allClasses]);

  const filteredClasses = useMemo(() => {
    if (!classes) return [];
    return filterClasses(classes, {
      grade: gradeTab, course: courseTab, time: timeTab, day: dayTab,
    }, searchStr, hideFullClasses);
  }, [classes, gradeTab, courseTab, timeTab, dayTab, searchStr, hideFullClasses]);

  useSuccess(isNewClassSuccess, 'Class created');
  useSuccess(isStudentDetailsSuccess, 'Copied successfully');

  if (isCoursesLoading || isStreamsLoading) {
    <SkeletonStack />;
  }

  return (
    <PageContent>
      {isAdmin ? (
        <FilterContainer>
          <Stack direction="column" spacing={2}>
            <Stack direction="row" alignItems="center">
              <Typography variant="subtitle1" width={80}>Day</Typography>
              <Tabs
                options={dayOptions}
                selectedKey={dayTab}
                maxOptions={8}
                onChange={(key) => setDayTab(key)}
                justifyContent="flex-start"
                dense
              />
            </Stack>
            <Stack direction="row" alignItems="center">
              <Typography variant="subtitle1" width={80}>Time</Typography>
              <Tabs
                options={timeOptions}
                selectedKey={timeTab}
                maxMobileOptions={3}
                onChange={(key) => setTimeTab(key)}
                justifyContent="flex-start"
                dense
              />
            </Stack>
            <Stack direction="row" alignItems="center">
              <Typography variant="subtitle1" width={80}>Grade</Typography>
              <Tabs
                options={[...INITIAL_TAB_OPTIONS, ...GRADES]}
                selectedKey={gradeTab}
                maxMobileOptions={3}
                onChange={(key) => setGradeTab(key)}
                justifyContent="flex-start"
                dense
              />
            </Stack>
            <Stack direction="row" alignItems="center">
              <Typography variant="subtitle1" width={80}>Course</Typography>
              <Tabs
                options={coursesOptions}
                selectedKey={courseTab}
                onChange={(key) => setCourseTab(key)}
                justifyContent="flex-start"
                dense
              />
            </Stack>
            <Grid container columnSpacing={2} alignItems="center">
              <Grid item xs>
                <SearchField
                  placeholder="Search..."
                  onSearch={(search) => setSearchStr(search)}
                />
              </Grid>
              <Grid item>
                <Button onClick={() => setNewClassFormOpen(true)}>
                  + Class
                </Button>
              </Grid>
              <Grid item>
                <IconButton
                  ref={menuRef}
                  onClick={() => setMenuOpen(true)}
                >
                  <CopyAllRounded />
                </IconButton>
              </Grid>
            </Grid>
          </Stack>
        </FilterContainer>
      ) : (<Typography variant="h6">Class Schedules</Typography>) }
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }} padding={1}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">Classes</Typography>
          <Stack direction="row" alignItems="center">
            <Typography variant="subtitle1">Hide full classes</Typography>
            <Switch
              checked={hideFullClasses}
              onChange={() => setHideFullClasses((prev) => !prev)}
            />
          </Stack>
        </Stack>
      </Box>
      <Box display="grid">
        <DataGrid
          loading={isLoading}
          getRowHeight={() => 'auto'}
          columns={[
            {
              field: 'courseLabel',
              headerName: 'Course',
              width: 140,
            },
            {
              field: 'streamLabel',
              headerName: 'Stream',
              width: 120,
            },
            {
              field: 'schoolTag',
              headerName: 'School',
              width: 120,
            },
            {
              field: 'dayLabel',
              headerName: 'Day',
              width: 120,
              // A custom sortComparator is needed as the lexical order of days
              // is not the order in which they appear in the week
              sortComparator: (v1, v2, param1, param2) =>
                compareDayLabels(param1.value, param2.value),
            },
            {
              field: 'timeLabel',
              headerName: 'Time',
              width: 120,
              // A custom sortComparator is needed as the lexical order of time
              // string isn't the order in which they appear in the day
              sortComparator: (v1, v2, param1, param2) => {
                const time1 = dayjs(param1.value, 'h:mm a');
                const time2 = dayjs(param2.value, 'h:mm a');

                return time1.valueOf() - time2.valueOf();
              },
            },
            {
              field: 'teacherName',
              headerName: 'Teacher',
              width: 140,
            },
            {
              field: 'studentCount',
              headerName: 'Students',
              renderHeader: () => (
                <GroupIcon />
              ),
              width: 60,
              renderCell: ({ row }: any) => (
                <Typography
                  color={theme.palette[getClassCountColor(row.studentCount)].dark}
                  variant="subtitle2"
                >
                  {row.studentCount} / {MAX_CLASS_SIZE}
                </Typography>
              ),
            },
            {
              field: 'notes',
              headerName: 'Notes/Waitlist',
              width: 120,
              renderCell: ({ row }: any) => {
                const hasClassNotes = row.notes && row.notes.length > 0;
                const hasWaitlist = row.waitlist && row.waitlist.length > 0;

                if (hasClassNotes || hasWaitlist) {
                  return (
                    <>
                      {hasClassNotes && <DescriptionIcon color="darkPrimary" fontSize="small" />}
                      {hasWaitlist && <HourglassBottomIcon color="darkPrimary" fontSize="small" />}
                    </>
                  );
                }

                return '-';
              },
            },
            {
              field: 'schedule',
              headerName: 'Schedule',
              renderHeader: () => (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Button size="small" onClick={() => setShowSchedule((prev) => !prev)}>
                    {showSchedule ? 'Hide schedule' : 'Show schedule'}
                  </Button>
                  {showSchedule && (
                    <DateNavigator
                      date={date}
                      onDateChange={setDate}
                      dateIncrement={7}
                      hideDateLabel
                    />
                  )}
                </Stack>
              ),
              width: showSchedule ? 640 : 360,
              renderCell: ({ row }: any) => {
                if (showSchedule) {
                  return (
                    <ClassScheduleView
                      date={date}
                      classObj={row}
                    />
                  );
                }
                return (
                  <Box height={48} />
                );
              },
            },
          ]}
          rows={filteredClasses || []}
          getRowHref={(row) => `/${isAdmin ? 'admin' : 'teacher'}/class/${row.subject}/${row.classId}`}
          apiRef={gridApiRef}
        />
      </Box>
      <DetailsModal
        open={newClassFormOpen}
        handleClose={() => setNewClassFormOpen(false)}
        title="Add New Class"
        fields={newClassFormFields}
        values={newClassForm}
        error={newClassError}
        buttons={[{
          fn: async (values?: Values) =>
          // TODO: Fix typing
            newClass(values as any),
          label: 'Add',
          color: 'darkPrimary',
          loading: isNewClassLoading,
        }]}
        onFieldChange={(key, value) => {
          setNewClassForm((prev) => ({
            ...prev,
            [key]: value,
          }));
        }}
      />
      <Menu
        anchorEl={menuRef.current}
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        onClick={() => setMenuOpen(false)}
        disableScrollLock
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem
          key="email"
          onClick={copyClassListToClipboard}
        >
          <ListItemIcon>
            <GroupIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2">
            Copy Classes
          </Typography>
        </MenuItem>
        <MenuItem
          key="email"
          onClick={() => copyStudentDetailsToClipboard('email')}
        >
          <ListItemIcon>
            <EmailRounded fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2">
            Copy Student Emails
          </Typography>
        </MenuItem>
        <MenuItem
          key="parentEmail"
          onClick={() => copyStudentDetailsToClipboard('parentEmail')}
        >
          <ListItemIcon>
            <EmailRounded fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2">
            Copy Parent Emails
          </Typography>
        </MenuItem>
        <MenuItem
          key="phoneNumber"
          onClick={() => copyStudentDetailsToClipboard('phoneNumber')}
        >
          <ListItemIcon>
            <PhoneRounded fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2">
            Copy Student Phone Numbers
          </Typography>
        </MenuItem>
        <MenuItem
          key="parentPhoneNumber"
          onClick={() => copyStudentDetailsToClipboard('parentPhoneNumber')}
        >
          <ListItemIcon>
            <PhoneRounded fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2">
            Copy Parent Phone Numbers
          </Typography>
        </MenuItem>
      </Menu>
      <StyledBackdrop open={isStudentDetailsFetching}>
        <CircularProgress />
      </StyledBackdrop>
    </PageContent>
  );
};
export default StaffClassesPage;
